import { Business } from "@mui/icons-material";
import {
  Avatar,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Typography,
} from "@mui/material";
import React from "react";
import type { Campus } from "../../types";
import { REGION_ORDER, getRegionForCampus } from "../../utils/dashboardHelpers";

interface CampusesTabProps {
  campuses: Campus[];
  loading: boolean;
  onCampusSelect: (campusId: string) => void;
}

const CampusesTab: React.FC<CampusesTabProps> = ({
  campuses,
  loading,
  onCampusSelect,
}) => {
  // Group campuses by region
  const campusesByRegion = campuses.reduce((acc, campus) => {
    const region = getRegionForCampus(campus.key);
    if (!acc[region]) {
      acc[region] = [];
    }
    acc[region].push(campus);
    return acc;
  }, {} as Record<string, Campus[]>);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Regional Groups */}
      {REGION_ORDER.map((region) => {
        const regionCampuses = campusesByRegion[region];
        if (!regionCampuses || regionCampuses.length === 0) return null;

        return (
          <Box key={region} sx={{ mb: 4 }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                mb: 3,
                color: "text.primary",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Business sx={{ color: "primary.main" }} />
              {region} ({regionCampuses.length})
            </Typography>

            <Grid container spacing={3}>
              {regionCampuses.map((campus) => (
                <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={campus.id}>
                  <Card
                    sx={{
                      height: "100%",
                      cursor: "pointer",
                      transition: "all 0.2s ease-in-out",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: "0 8px 25px rgba(137, 44, 139, 0.15)",
                      },
                    }}
                    onClick={() => onCampusSelect(campus.id)}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 2,
                          mb: 2,
                        }}
                      >
                        <Avatar
                          sx={{
                            width: 40,
                            height: 40,
                            bgcolor: "primary.main",
                            color: "white",
                          }}
                        >
                          <Business />
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 600,
                              mb: 0.5,
                              fontSize: "1rem",
                            }}
                          >
                            {campus.name}
                          </Typography>
                          {/* <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 1 }}
                          >
                            {campus.address}
                          </Typography> */}
                          <Typography variant="body2" color="text.secondary">
                            {campus.childrenCount} children
                          </Typography>
                        </Box>
                      </Box>

                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          pt: 2,
                          borderTop: "1px solid",
                          borderColor: "divider",
                        }}
                      >
                        <Typography
                          variant="body2"
                          color="primary.main"
                          sx={{
                            fontWeight: 500,
                          }}
                        >
                          View Details
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        );
      })}

      {/* Empty state if no campuses */}
      {campuses.length === 0 && !loading && (
        <Box sx={{ textAlign: "center", py: 8 }}>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
            No campuses found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Start by adding your first campus
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default CampusesTab;
